import './BranchRegistrationWorkflowSearchResult.pcss';

import { CloseOutlined, LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { Spin, Tag } from 'antd';
import { FC, useState } from 'react';

import { BranchRegistrationWorkflowDto } from 'api/organization/onboardingTypes';

const BranchTag: FC<{
  branch: string;
  taken: boolean;
  excluded?: boolean;
  manageable: boolean;
  onManage: () => Promise<void>;
}> = ({ branch, taken, excluded, manageable, onManage }) => {
  const [isLoading, setIsLoading] = useState(false);
  return (
    <Tag
      className={`facility-tag ${taken ? 'taken' : ''} ${excluded ? 'excluded' : ''}`}
      closable={manageable}
      closeIcon={(() => {
        if (isLoading) {
          return <Spin indicator={<LoadingOutlined style={{ fontSize: 18 }} spin />} />;
        }
        return excluded ? <PlusOutlined /> : <CloseOutlined />;
      })()}
      onClose={async (e) => {
        e.preventDefault();
        if (isLoading) {
          return;
        }
        setIsLoading(true);
        await onManage();
        setIsLoading(false);
      }}
    >
      {branch}
    </Tag>
  );
};

interface BranchRegistrationWorkflowSearchResultProps {
  branchesTaken: string[];
  branchRegistrationWorkflow: BranchRegistrationWorkflowDto;
  branchRegistrationWorkflowCanBeManaged: boolean;
  onExcludeBranchFromRegistrationWorkflow: (id: string, branch: string) => Promise<void>;
  onIncludeBranchToRegistrationWorkflow: (id: string, branch: string) => Promise<void>;
}

export const BranchRegistrationWorkflowSearchResult: FC<BranchRegistrationWorkflowSearchResultProps> = ({
  branchesTaken,
  branchRegistrationWorkflow,
  branchRegistrationWorkflowCanBeManaged,
  onExcludeBranchFromRegistrationWorkflow,
  onIncludeBranchToRegistrationWorkflow,
}) => {
  if (branchRegistrationWorkflow.error) {
    return (
      <div>
        <span>{branchRegistrationWorkflow.error}</span>
      </div>
    );
  }
  if (branchRegistrationWorkflow.foundProviders == null) {
    return (
      <div className="retrieving-status">
        <Spin indicator={<LoadingOutlined style={{ fontSize: 18 }} spin />} />
        <span>Retrieving facilities...</span>
      </div>
    );
  }
  return (
    <div className="facility-tags">
      {branchRegistrationWorkflow.foundProviders
        .filter((branch) => !branchRegistrationWorkflow.excludedProviders?.includes(branch))
        .map((branch) => (
          <BranchTag
            key={branch}
            branch={branch}
            taken={branchesTaken.includes(branch)}
            manageable={branchRegistrationWorkflowCanBeManaged && branchRegistrationWorkflow.upsertedProviders == null}
            onManage={async () => await onExcludeBranchFromRegistrationWorkflow(branchRegistrationWorkflow.id, branch)}
          />
        ))}
      {branchRegistrationWorkflow.excludedProviders?.map((branch) => (
        <BranchTag
          key={branch}
          branch={branch}
          taken={branchesTaken.includes(branch)}
          excluded
          manageable={branchRegistrationWorkflowCanBeManaged && branchRegistrationWorkflow.upsertedProviders == null}
          onManage={async () => await onIncludeBranchToRegistrationWorkflow(branchRegistrationWorkflow.id, branch)}
        />
      ))}
    </div>
  );
};
